import { mwGet, mwPost, mapResponse, requireId } from "./client";
import { streamUrl, registerPreview } from "./playback";
import { readCache } from "../../utils/readCache";
import { isComingSoon, publicationStart } from "../../utils/publication";
import { mapRentalOffer, isRentalOffer, rentalCopy } from "./rental";

export const posters = (items = [], fallback) => {
  const mapped = items.map((p) => ({
    type: p.Type,
    url: p.ImageUrl,
    posterType: p.Type,
    posterUrl: p.ImageUrl
  }));
  for (const type of ["Web Portrait", "Web Landscape", "Web Banner"]) {
    if (!mapped.some((p) => p.type === type)) {
      const match = items.find((p) =>
        type === "Web Portrait"
          ? /portrait|vertical|listing/i.test(p.Type)
          : /landscape|horizontal|detail|preview/i.test(p.Type)
      );
      if (match || fallback)
        mapped.push({ type, url: match?.ImageUrl || fallback });
    }
  }
  return mapped;
};
export function mapVod(v) {
  return {
    ...v,
    id: v.UId || v.ContentUId || v.VodUId,
    title: v.Title || v.Name,
    type:
      String(v.ContentType).toUpperCase() === "CATEGORY"
        ? "CATEGORY"
        : String(v.ContentType).toUpperCase() === "LIVE"
        ? "CHANNEL"
        : (v.VodType || v.Type || "MOVIE").toUpperCase(),
    channelId:
      String(v.ContentType).toUpperCase() === "LIVE"
        ? v.ContentUId
        : v.ChannelUId,
    description: v.Description,
    summary: v.Description,
    year: v.ReleaseYear,
    duration: v.Duration,
    lastDuration: v.PausedTime ?? v.PauseTime ?? 0,
    seasonNo: v.SeasonNumber,
    episodeNo: v.EpisodeNumber,
    isUnlocked: v.IsAvailableToWatch === true && !isComingSoon(v),
    isComingSoon: isComingSoon(v),
    publicationDate: publicationStart(v),
    requirePinCode: v.RequirePinCode,
    posters: posters(
      v.Posters,
      v.PosterImageUrl || v.PosterUrl || v.ThumbnailUrl
    ),
    maturityRating: {
      age: v.IsPlus18Audience
        ? "18+"
        : v.IsPlus13Audience
        ? "13+"
        : v.IsPlus7Audience
        ? "7+"
        : "G",
      categories: []
    },
    actors: (v.Cast || []).map((c) => ({
      name: c.FullName || c.Name,
      type: c.Type
    })),
    genres: (v.Genres || []).map((g) => ({ id: g.UId, name: g.Name })),
    casts: (v.Cast || []).map((c) => ({
      ...c,
      name: c.FullName || c.Name,
      type: c.Type
    })),
    // PURCHASE has no MW client transaction contract. Never turn it into a rental.
    purchaseUnavailable: v.IsAvailableToBuy === true || !!v.PurchaseDetails?.length,
    products: v.IsAvailableToWatch === true || v.IsAvailableToRent !== true ? []
      : (v.RentDetails || []).map(mapRentalOffer).filter(isRentalOffer),
    interactions: [
      ...(v.IsInDefaultPlaylist ? [{ type: "VOD_WATCHLIST" }] : []),
      ...(v.IsInFavorites ? [{ type: "VOD_FAV" }] : []),
      ...(v.ReactionValue === 1 ? [{ type: "VOD_LIKE" }] : []),
      ...(v.ReactionValue === 5 ? [{ type: "VOD_DISLIKE" }] : [])
    ]
  };
}
const navigation = (options) =>
  mwGet("/catalog/navigation-categories", undefined, options);
export const home = (options) => mwGet("/catalog/home", undefined, options);
export const category = (id, options) =>
  mwGet(
    "/catalog/category-contents",
    {
      CategoryUId: requireId(id),
      SortBy: "title",
      SortDirection: "asc",
      showAll: true
    },
    options
  );
const isRootCategory = (category) => {
  const parent =
    category.ParentCategoryUId ??
    category.ParentUId ??
    category.ParentCategory ??
    category.parentCategory ??
    category.parentUid;
  if (parent) return false;
  // The MW navigation DTO omits parent information. When Level is supplied,
  // its absence of a parent field must not turn level 1/2 into menu items.
  return category.Level == null || Number(category.Level) === 0;
};
const childLists = (data, parentId) =>
  data.HasSubcategory
    ? (data.Lists || []).filter((list) => list.UId && list.UId !== parentId)
    : [];
const directContents = (data, id) =>
  data.HasSubcategory
    ? []
    : (data.Lists || [])
        .filter((list) => list.UId === id)
        .flatMap((list) => list.Items || [])
        .map(mapVod);

const imageUrl = (...urls) =>
  urls
    .find((url) => typeof url === "string" && url.trim() && url.trim() !== "-")
    ?.trim();

async function menuBanners(data, id, options) {
  const artwork = imageUrl(data.LargeImageUrl, data.BackgroundImageUrl);
  const menuBanner = {
    id,
    title: data.Name,
    type: "CATEGORY",
    posters: artwork ? posters([], artwork) : []
  };
  if (artwork) return [menuBanner];

  // Category responses have no Slides. Build the showcase from this menu's
  // visible contents, keeping the highlighted content first and removing repeats.
  const candidates = [
    data.HighlightedItem,
    ...(data.Lists || []).flatMap((list) => list.Items || [])
  ].filter(
    (item) =>
      item && (!item.ContentType || item.ContentType.toUpperCase() === "VOD")
  );
  const unique = new Map();
  candidates.forEach((item) => {
    const uid = item.UId || item.ContentUId || item.VodUId;
    if (uid && !unique.has(uid)) unique.set(uid, item);
  });
  if (!unique.size) return [menuBanner];

  return Promise.all(
    [...unique.values()].slice(0, 6).map(async (item) => {
      const mapped = mapVod(item);
      let wideImage;
      try {
        // Listing posters are usually portraits. Keep only artwork in the scoped
        // cache, never the detail response's playback URLs or DRM credentials.
        const images = await readCache.read(
          `mw-banner-posters:${mapped.id}`,
          async () => {
            const response = await rawVod(
              mapped.id,
              {
                ...options,
                timeout: Math.min(options?.timeout || 5000, 5000)
              },
              true
            );
            const detail =
              response.data.find((vod) => vod.UId === mapped.id) ||
              response.data[0];
            return (detail.Posters || []).map(({ Type, ImageUrl }) => ({
              Type,
              ImageUrl
            }));
          },
          30000
        );
        wideImage = imageUrl(
          ...images
            .filter((p) => /banner|background/i.test(p.Type))
            .map((p) => p.ImageUrl),
          ...images
            .filter((p) => /landscape|horizontal|detail|preview/i.test(p.Type))
            .map((p) => p.ImageUrl)
        );
      } catch (error) {
        if (error.code === "STALE_CONTEXT") throw error;
        // Optional artwork enrichment must not prevent a menu from opening.
      }
      return {
        ...mapped,
        posters: wideImage ? posters([], wideImage) : mapped.posters
      };
    })
  );
}

export const menus = async (options) =>
  mapResponse(await navigation(options), (list) => [
    { id: "mw-home", name: "Ana Sayfa", isMain: true },
    ...list
      .filter((c) => c.UId && c.IsVodContentMenu && isRootCategory(c))
      .map((c) => ({ id: c.UId, name: c.Name, main: false }))
  ]);
export async function categories(id, options) {
  if (id === "mw-home")
    return mapResponse(await home(options), (data) => ({
      name: "Ana Sayfa",
      categories: [{ id, name: "Ana Sayfa", isMain: true }],
      banners: (data.Slides || [])
        .filter((s) => s.ContentUId)
        .map((s) => ({
          ...mapVod({ ...s, Title: s.Name }),
          type:
            String(s.ContentType).toUpperCase() === "LIVE"
              ? "PROGRAM"
              : mapVod(s).type,
          posters: posters([], s.WebImageUrl || s.LargeImageUrl),
          mobileImageUrl: s.MobileImageUrl
        }))
    }));
  const response = await category(id, options);
  const banners = await menuBanners(response.data, id, options);
  return mapResponse(response, (data) => ({
    name: data.Name || "",
    // A level 0 request returns its direct level 1 children in Lists.
    categories: childLists(data, id).map((l) => ({ id: l.UId, name: l.Name })),
    banners,
    contents: directContents(data, id)
  }));
}
export async function labels(id, options) {
  const isHome = id === "mw-home";
  const response = await (isHome ? home(options) : category(id, options));
  return mapResponse(response, (data) => ({
    // A level 1 request returns level 2 shelves; a leaf's self-list is content,
    // not a new category level. Home widgets are a separate MW showcase.
    labels: (isHome ? data.Widgets || [] : childLists(data, id)).flatMap(
      (l, index) => {
        // Preserve source indexes: shelf() resolves mw-widget IDs against Widgets.
        if (
          !Array.isArray(l.Items) ||
          (isHome && l.WidgetType === "CHANNEL_LIST")
        )
          return [];
        return [
          {
            id: isHome
              ? l.WidgetType === "VOD_CATEGORY" &&
                l.TargetUId &&
                !/^0{8}-0{4}-0{4}-0{4}-0{12}$/.test(l.TargetUId)
                ? l.TargetUId
                : `mw-widget:${index}`
              : l.UId,
            name: l.Title || l.Name,
            title: l.Title || l.Name,
            priority: -(l.DisplayOrder ?? index),
            type: "MW",
            vertical: true,
            contents: l.Items.map(mapVod)
          }
        ];
      }
    ),
    contents: isHome ? [] : directContents(data, id)
  }));
}
export async function shelf(id, page = 0, size = 20, options) {
  let response, items;
  const widget = /^mw-widget:(\d+)$/.exec(id),
    list = /^mw-list:([^:]+):(\d+)$/.exec(id);
  if (widget) {
    response = await readCache.read("mw-home", () => home(options), 30000);
    items = response.data.Widgets?.[Number(widget[1])]?.Items || [];
  } else {
    const categoryId = list ? list[1] : id;
    response = await readCache.read(
      `mw-category:${categoryId}`,
      () => category(categoryId, options),
      30000
    );
    items = list
      ? response.data.Lists?.[Number(list[2])]?.Items || []
      : (response.data.Lists || []).flatMap((l) => l.Items || []);
  }
  items = Array.from(
    new Map(
      items.map((item) => {
        const mapped = mapVod(item);
        return [mapped.id, mapped];
      })
    ).values()
  );
  return {
    ...response,
    data: {
      contents: items.slice(page * size, (page + 1) * size),
      totalContents: items.length,
      vertical: true
    }
  };
}
export async function rawVod(id, options, preview = false) {
  const response = await mwGet(
    "/vod/detail",
    { VodUId: requireId(id), AsPreview: preview },
    options
  );
  if (!Array.isArray(response.data) || !response.data.length)
    throw new Error("MW içerik detayı geçersiz.");
  return response;
}
export async function detail(id, options) {
  const season = /^mw-season:([^:]+):(\d+)$/.exec(id);
  const response = await rawVod(season ? season[1] : id, options);
  const parent =
    response.data.find((v) => v.UId === (season ? season[1] : id)) ||
    response.data[0];
  // MW intentionally omits Seasons from its client DTO; group Data[] episodes.
  const grouped = new Map();
  response.data
    .filter((v) => v.SeasonNumber > 0 || v.EpisodeNumber > 0)
    .forEach((v) => {
      const number = v.SeasonNumber || 1;
      if (!grouped.has(number))
        grouped.set(number, { SeasonNumber: number, Episodes: [] });
      grouped.get(number).Episodes.push(v);
    });
  const seasons =
    parent.Seasons ||
    [...grouped.values()].sort((a, b) => a.SeasonNumber - b.SeasonNumber);
  seasons.forEach((s) =>
    s.Episodes.sort((a, b) => a.EpisodeNumber - b.EpisodeNumber)
  );
  const episodeData = (e) =>
    mapVod({
      ...e,
      ...response.data.find((v) => v.UId === e.UId),
      Type: "EPISODE"
    });
  if (season)
    return {
      ...response,
      data: {
        id,
        title: `${season[2]}. Sezon`,
        type: "SEASON",
        children: (
          seasons.find((s) => s.SeasonNumber === Number(season[2]))?.Episodes ||
          []
        ).map(episodeData)
      }
    };
  const mapped = mapVod(parent);
  if (seasons.length) {
    mapped.id = id;
    mapped.title = parent.ParentTitle || parent.Title;
    mapped.type = "SERIES";
    mapped.children = seasons.map((s) => ({
      id: `mw-season:${id}:${s.SeasonNumber}`,
      title: `${s.SeasonNumber}. Sezon`,
      type: "SEASON",
      seasonNo: s.SeasonNumber,
      children: (s.Episodes || []).map(episodeData)
    }));
  }
  return { ...response, data: mapped };
}
export async function playVod(id) {
  const response = await rawVod(id);
  const item =
    response.data.find((v) => v.UId === id) ||
    response.data.find((v) => v.IsSelectedEpisode) ||
    response.data[0];
  if (isComingSoon(item)) throw new Error("Bu içerik henüz yayında değil.");
  if (item.IsAvailableToWatch !== true)
    throw Object.assign(new Error(rentalCopy().unavailable), {
      code: "CONTENT_UNAVAILABLE", content: mapVod(item)
    });
  return {
    ...response,
    data: {
      url: streamUrl(item.StreamData),
      streamData: item.StreamData,
      lastDuration: item.PausedTime
    }
  };
}
export async function trailer(id) {
  const response = await rawVod(id, undefined, true);
  const item = response.data.find((v) => v.UId === id) || response.data[0];
  // Unpublished titles must never preview the full programme stream.
  if (isComingSoon(item)) throw new Error("Bu içerik için bağımsız fragman henüz hazır değil.");
  if (
    item.ProviderPreferences?.ShowTrailersInDetails === false ||
    item.IsAvailableToWatch === false ||
    item.RequirePinCode === true
  )
    throw new Error("Bu içerik için önizleme kullanılamıyor.");

  const duration = Number(item.Duration);
  const limit = Number.isFinite(duration) && duration > 0 ? duration : Infinity;
  const range = [item, ...(Array.isArray(item.Trailers) ? item.Trailers : [])]
    .map((trailer) => ({
      start: Number(trailer?.TrailerStartSecond ?? 0),
      end: Math.min(Number(trailer?.TrailerEndSecond ?? 0), limit)
    }))
    .find(({ start, end }) =>
      Number.isFinite(start) && Number.isFinite(end) && start >= 0 && end > start
    );
  // The legacy trailer endpoint used this same VOD stream. Preserve its moving
  // preview for titles without markers, but always bound it to a short excerpt.
  const { start, end } = range || { start: 0, end: Math.min(30, limit) };
  const url = streamUrl(item.StreamData);
  registerPreview(url, start, end);
  return { ...response, data: url };
}
export async function toggleWatchlist(id) {
  const response = await mwGet("/playlists/default");
  const playlist = response.data;
  const exists = playlist.Items?.some((i) => i.VodUId === id);
  return mwPost(exists ? "/playlists/remove-item" : "/playlists/add-item", {
    PlaylistUId: playlist.UId,
    VodUId: id
  });
}
export async function watchProgress(id, time) {
  if (!Number.isFinite(Number(time)) || Number(time) < 0)
    throw new Error("İzleme süresi geçersiz.");
  return mwPost("/statistics/vod-watch", {
    VodUId: requireId(id),
    PauseTime: Math.floor(Number(time)),
    WatchedTime: 0
  });
}
export async function completed(id, duration) {
  // Completion is derived from position by MW; a toggle would undo it on retry.
  if (!Number.isFinite(duration)) duration = (await detail(id)).data.duration;
  if (!Number.isFinite(duration) || duration <= 0)
    throw new Error("İçerik süresi alınamadı.");
  return watchProgress(id, duration);
}
